import { ConfigStateService, LocalizationService, PermissionService } from '@abp/ng.core';
import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { PrimengTableHelper } from './helpers/PrimengTableHelper';
import { AppConsts } from './AppConsts';
import { Confirmation, ConfirmationService, Toaster, ToasterService } from '@abp/ng.theme.shared';
import { firstValueFrom } from 'rxjs';
import { EnumDescriptor } from './enum-descriptor';

@Component({
    template: '',
    standalone: false
})
export abstract class AppBaseComponent {
    
    readonly primengTableHelper: PrimengTableHelper;
    readonly Hyphen = AppConsts.Hyphen;
    
    protected readonly titleService: Title;
    protected readonly permission: PermissionService;
    
    private readonly localization: LocalizationService;
    private readonly spinner: NgxSpinnerService;

    private readonly toaster: ToasterService;
    private readonly confirmation: ConfirmationService;

    protected readonly setting: ConfigStateService;

    private isPointerDown = false;
    private pointerStartX = 0;
    private scrollStart = 0;
    private readonly forceHorizontal = 'force-horizontal';
    private readonly classesWhenResize = '.no-drag, button, a, input, textarea, select, label, [role="button"], .p-button';
    protected resizeHandler: () => void;
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLDivElement>;

    constructor(injector: Injector) {
        this.titleService = injector.get(Title);
        this.localization = injector.get(LocalizationService);
        this.spinner = injector.get(NgxSpinnerService);
        this.permission = injector.get(PermissionService);
        this.toaster = injector.get(ToasterService);
        this.confirmation = injector.get(ConfirmationService);
        this.setting = injector.get(ConfigStateService);

        this.primengTableHelper = new PrimengTableHelper();
    }

    protected l(key: string, ...args: any[]): string {
        let localizedText = this.localization.instant(key, ...args);

        if (!localizedText) {
          localizedText = key;
        }
    
        return localizedText;
    }

    protected setTitle(title: string): void {
        this.titleService.setTitle(title);
    }

    protected getDefaultTitle(): string {
        return `${AppConsts.DefaultTitle}`;
    }

    protected showLoading(): void {
        this.spinner.show();
      }
    
    protected hideLoading(): void {
        this.spinner.hide();
    }

    protected hasPermission(permissionName: string): boolean {
        return this.permission.getGrantedPolicy(permissionName);
    }
    
    protected notifySuccess(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.success(message, title, options);
    }
    
    protected notifyInfo(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.info(message, title, options);
    }
    
    protected notifyError(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.error(message, title, options);
    }
    
    protected notifyWarn(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.warn(message, title, options);
    }    

    protected isNullOrUndefined(value: any): boolean {
        return value === null || value === undefined;
    }

    isEmptyString(value: string): boolean {
        return this.isNullOrUndefined(value) || value === '' || value.toString().trim().length === 0;
    }

    protected async confirmAndExecute<T>(message: string, confirmedAction: () => T,
        messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []
    ): Promise<void> {
        const options = this.generateOptionsParamsForConfirm(messageLocalizationParams, titleLocalizationParams);
        const statusResponse = await firstValueFrom(
            this.confirmation.warn(message, '::AreYouSureToPerformThisAction', options)
        );
    
        if (statusResponse === Confirmation.Status.confirm) {
            await confirmedAction();
        }
    }

    protected startDrag(event: PointerEvent) {
        const target = event.target as HTMLElement;
    
        if (target?.closest?.(this.classesWhenResize)) {
          return;
        }
    
        this.isPointerDown = true;
        this.pointerStartX = event.clientX;
    
        if (this.scrollContainer?.nativeElement) {
          this.scrollStart = this.scrollContainer.nativeElement.scrollLeft;
        }
    
        target.setPointerCapture?.(event.pointerId);
    }

    protected onDrag(event: PointerEvent) {
        if (!this.isPointerDown) {
            return;
        }

        const dx = event.clientX - this.pointerStartX;

        if (this.scrollContainer?.nativeElement) {
            this.scrollContainer.nativeElement.scrollLeft = this.scrollStart - dx;
        }
    }

    protected stopDrag(event: PointerEvent) {
        this.isPointerDown = false;
        const target = event.target as HTMLElement;
        target.releasePointerCapture?.(event.pointerId);
    }

    protected updateScrollWrapperClass(): void {
        const wrapperElement = this.scrollContainer?.nativeElement as HTMLElement;
        if (!wrapperElement) {
        return;
        }

        if (wrapperElement.scrollWidth > wrapperElement.clientWidth) {
        wrapperElement.classList.add(this.forceHorizontal);
        } else {
        wrapperElement.classList.remove(this.forceHorizontal);
        }
    }

    protected resizeOnDestroy(): void {        
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
    }

    protected resizeAfterViewInit() {
        this.updateScrollWrapperClass();

        this.resizeHandler = () => this.updateScrollWrapperClass();
        window.addEventListener('resize', this.resizeHandler);
    }

    private generateOptionsParamsForNotify(messageLocalizationParams: string[], titleLocalizationParams: string[]): Partial<Toaster.ToastOptions> {
        const options: Partial<Toaster.ToastOptions> = {
            life: 8000,
            sticky: false,
            closable: true,
            tapToDismiss: true,
            messageLocalizationParams: messageLocalizationParams,
            titleLocalizationParams: titleLocalizationParams,
        };

        return options;
    }

    private generateOptionsParamsForConfirm(messageLocalizationParams: string[], titleLocalizationParams: string[]): Partial<Toaster.ToastOptions> {
        const options: Partial<Confirmation.Options> = {
            hideCancelBtn: false,
            hideYesBtn: false,
            dismissible: false,
            cancelText: '::Close',
            yesText: '::Confirm',
            messageLocalizationParams: messageLocalizationParams,
            titleLocalizationParams: titleLocalizationParams,
          };
        
        return options;
    }

    protected getEnumOptions(enumObj: any, addAllOption = false): EnumDescriptor[] {
        let items = [];
        if (addAllOption) {
            items.push({
                label: this.l('::All'),
                value: -1,
            });
        }

        items = items.concat(
            Object.keys(enumObj)
                .filter(key => isNaN(Number(key)))
                .map(key => ({ label: this.l(`::${key}`), value: enumObj[key] }))
        );

        return items;
    }

    protected parseDateFromString(timeString: string): Date {
        const today = new Date();
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        today.setHours(hours, minutes, seconds || 0, 0);
        return new Date(today);
    }

    protected formatToHHMM(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }
    
    protected isDefinedAndStrictlyPositiveNumber(value: number): boolean {
        return this.isDefinedAndIsANumber(value) && value > 0;
    }

    protected isDefinedAndPositiveNumber(value: number): boolean {
        return this.isDefinedAndIsANumber(value) && value >= 0;
    }
    
    protected isDefined(value: any): boolean {
        return !this.isNullOrUndefined(value);
    }

    protected wrapText(text: string, maxLength: number = AppConsts.DefaultCharactersForWrap): string {
        if (!text) {
            return '';
        }
        
        const regex = new RegExp(`(?![^\\n]{1,${maxLength}}$)([^\\n]{1,${maxLength}})\\s`, 'g');
        return text.replace(regex, '$1<br>');
    }
    
    private isDefinedAndIsANumber(value: number): boolean {
        return this.isDefined(value) && !isNaN(value);
    }
}